import {JobPostingRequest} from '../interface/JobPostingRequest';
import {SeniorFormData} from "../interface/SeniorFormData";

interface JobQuestion {
    questionCode: string;
    questionName: string;
    questionType: 'O' | 'T' | 'M';
    required?: boolean; // 필수 여부
    options?: { optionName: string }[];
}


export const shouldShowMoreButton = (
    currentPage: number,
    pageSize: number,
    totalSize: number, // 조회 후 나온 count값
    maxSize: number // 검색시 설정한 사이즈
): boolean => {

    // 총 페이지가 1페이지면 숨김
    if (currentPage >= pageSize) {
        return false;
    }
    // 현재까지 로드된 아이템 수
    const loadedItems = maxSize * currentPage;
    // 아직 더 가져올 데이터가 있으면 표시
    return loadedItems < totalSize;
};

/**
 * 생년월일 유효성 검사 (yyyyMMdd 형식)
 * - 8자리 숫자 형식 체크
 * - 실제 존재하는 날짜인지 확인 (윤년, 월별 일수 등)
 * - 미래 날짜 불가
 * - 나이 범위 제한 (minAge ~ maxAge)
 */
export const validateBirthday = (birthday: string, minAge: number = 0, maxAge: number = 120): string | null => {
    if (!birthday) {
        return '생년월일을 입력해주세요.';
    }
    if (!/^\d{8}$/.test(birthday)) {
        return '생년월일은 yyyyMMdd 형식(숫자 8자리)으로 입력해주세요.';
    }

    const year = parseInt(birthday.substring(0, 4), 10);
    const month = parseInt(birthday.substring(4, 6), 10);
    const day = parseInt(birthday.substring(6, 8), 10);

    // 월 범위 체크
    if (month < 1 || month > 12) {
        return '생년월일의 월이 올바르지 않습니다. (01~12)';
    }

    // 해당 월의 마지막 일자 계산 (윤년 자동 반영)
    const lastDay = new Date(year, month, 0).getDate();
    if (day < 1 || day > lastDay) {
        return `생년월일의 일이 올바르지 않습니다. (${month}월은 1~${lastDay}일)`;
    }

    // 실제 날짜 객체로 변환하여 유효성 재확인
    const birthDate = new Date(year, month - 1, day);
    if (birthDate.getFullYear() !== year || birthDate.getMonth() !== month - 1 || birthDate.getDate() !== day) {
        return '올바른 생년월일을 입력해주세요.';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // 미래 날짜 체크
    if (birthDate > today) {
        return '생년월일은 미래 날짜일 수 없습니다.';
    }

    // 나이 계산
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }

    if (age < minAge) {
        return `나이는 ${minAge}세 이상이어야 합니다.`;
    }
    if (age > maxAge) {
        return `나이는 ${maxAge}세 이하여야 합니다.`;
    }

    return null; // 유효
};

export const validateStep1 = (formData: JobPostingRequest): string[] => {
    const errors: string[] = [];

    // 필수 필드 검증
    if (!formData.elderName?.trim()) {
        errors.push('어르신 성명을 입력해주세요.');
    }
    if (!formData.gender) {
        errors.push('성별을 선택해주세요.');
    }
    // 생년월일 검증 (어르신 대상: 0세 ~ 120세)
    const birthdayError = validateBirthday(formData.birthday, 0, 120);
    if (birthdayError) {
        errors.push(birthdayError);
    }
    if (!formData.weight) {
        errors.push('몸무게를 입력해주세요.');
    }
    if (!formData.height) {
        errors.push('키를 입력해주세요.');
    }

    if (!formData.relationship) {
        errors.push('관계를 선택해주세요.');
    }
    if (formData.weight && isNaN(Number(formData.weight))) {
        errors.push('몸무게는 숫자만 입력 가능합니다.');
    }
    if (formData.height && isNaN(Number(formData.height))) {
        errors.push('키는 숫자만 입력 가능합니다.');
    }


    return errors;
};


export const validateStep2 = (
    formData: JobPostingRequest,
    questionList: JobQuestion[],
    answers: Record<string, string | { selection: string; text: string }>
): string[] => {
    const errors: string[] = [];
    if (!formData.serviceType) {
        errors.push('서비스 유형을 선택해주세요.');
    }
    if (!formData.workType) {
        errors.push('업무 유형을 선택해주세요.');
    }
    if (formData.workType === 'HOURLY') {
        if (!formData.workStartTime) {
            errors.push('업무 시작 시간을 선택해주세요.');
        }
        if (!formData.workEndTime) {
            errors.push('업무 종료 시간을 선택해주세요.');
        }
    }
    if (!formData.careStartDate) {
        errors.push('돌봄 시작 날짜를 선택해주세요.');
    }
    if (!formData.careEndDate) {
        errors.push('돌봄 종료 날짜를 선택해주세요.');
    }
    /*  if (!formData.careHours) {
          errors.push('돌봄 시간을 선택해주세요.');
      }*/
    if (!formData.carePlaceType) {
        errors.push('돌봄 장소 유형을 선택해주세요.');
    }
    if (!formData.address?.trim()) {
        errors.push('주소를 입력해주세요.');
    }
    if (!formData.addressDetail?.trim()) {
        errors.push('상세 주소를 입력해주세요.');
    }

    // 조건부 필드 검증
    if (formData.carePlaceType === 'HOSPITAL' && !formData.hospitalName?.trim()) {
        errors.push('병원명을 입력해주세요.');
    }

    if (!formData.dailyPay) {
        errors.push('일급을 입력해주세요.');
    }

    // 날짜 검증
    if (formData.careStartDate && formData.careEndDate) {
        const startDate = new Date(formData.careStartDate);
        const endDate = new Date(formData.careEndDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // 시작 날짜가 오늘 이전인지 확인
        if (startDate < today) {
            errors.push('돌봄 시작 날짜는 오늘 이후여야 합니다.');
            return errors; // 여기서 리턴
        }
        if (startDate > endDate) {
            errors.push('돌봄 시작 날짜가 종료 날짜보다 클 수 없습니다.');
        }
    }
    if (errors.length === 0) {
        questionList.forEach((question) => {
            const answer = answers[question.questionCode];

            if (question.required) {
                if (!answer) {
                    errors.push(`${question.questionName}을(를) 입력해주세요.`);
                } else if (question.questionType === 'M') {
                    // 복합식: selection 체크
                    if (typeof answer === 'object' && !answer.selection?.trim()) {
                        errors.push(`${question.questionName}을(를) 선택해주세요.`);
                    }
                } else {
                    // 객관식/주관식: 문자열 체크
                    if (typeof answer === 'string' && !answer.trim()) {
                        errors.push(`${question.questionName}을(를) 입력해주세요.`);
                    }
                }
            }
        });
    }


    if (formData.dailyPay && isNaN(Number(formData.dailyPay))) {
        errors.push('일급은 숫자만 입력 가능합니다.');
    }

    return errors;
}

export const validateSeniorForm = (formData: SeniorFormData): string[] => {
    const errors: string[] = [];

    if (!formData.name?.trim()) {
        errors.push('어르신 이름을 입력해주세요.');
    }
    // 생년월일 검증 (어르신 대상: 0세 ~ 120세)
    const birthdayError = validateBirthday(formData.birthday, 0, 120);
    if (birthdayError) {
        errors.push(birthdayError);
    }
    if (!formData.gender) {
        errors.push('성별을 선택해주세요.');
    }
    if (!formData.height) {
        errors.push('키를 입력해주세요.');
    }
    if (formData.height && isNaN(Number(formData.height))) {
        errors.push('키는 숫자만 입력 가능합니다.');
    }
    if (!formData.weight) {
        errors.push('체중을 입력해주세요.');
    }
    if (formData.weight && isNaN(Number(formData.weight))) {
        errors.push('체중은 숫자만 입력 가능합니다.');
    }
    if (!formData.relationship) {
        errors.push('관계를 선택해주세요.');
    }

    return errors;
};

// 비밀번호 유효성 검사 (영문 대소문자/숫자/특수문자 중 2가지 이상 조합, 8~20자)
export const validatePassword = (password: string): boolean => {
    if (password.length < 8 || password.length > 20) {
        return false;
    }

    let count = 0;
    if (/[a-z]/.test(password)) count++; // 소문자
    if (/[A-Z]/.test(password)) count++; // 대문자
    if (/[0-9]/.test(password)) count++; // 숫자
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) count++; // 특수문자

    return count >= 2;
};

export const isNullOrEmpty = (value: any):boolean => {
    switch (value) {
        case "":
        case null:
        case false:
        case undefined:
        case typeof this === 'undefined':
            return true;
        default:
            return false;
    }
};